import { useQuery } from "@apollo/client";
import { CircularProgress, Typography } from "@mui/material";
import React, { useMemo } from "react";

import { ExerciseId } from "../types";
import { getAiExerciseNameFromId, isAiExerciseId, isTraditionalExerciseId } from "../utils/exercises";
import { isDefined } from "../utils/isDefined";
import { EditListItem } from "./EditListItem";
import { exercisesByIdsQuery, GetExercisesByIDsQueryType, GetExercisesByIDsQueryVariables } from "./GetExerciseByIds.gql";

interface Props {
    ids: ReadonlyArray<ExerciseId>;
    header: React.ReactNode;
    noExercisePlaceholder: React.ReactNode;
    onDelete: (id: ExerciseId) => void;
}

const ExercisesEditList: React.FC<Props> = ({ ids, header, noExercisePlaceholder, onDelete }) => {
    // Using getFavouritesIds because user cannot add new favourites here, and therefore no reload of data is needed
    const variables = useMemo<GetExercisesByIDsQueryVariables>(() => ({ ids: ids.filter(isTraditionalExerciseId) }), [ids]);

    const { data, loading } = useQuery<GetExercisesByIDsQueryType, GetExercisesByIDsQueryVariables>(exercisesByIdsQuery, {
        variables,
        skip: variables.ids.length === 0,
    });

    const traditionalExercise = useMemo(() => data?.exercisesByIds.map((exercise) => ({ ...exercise, isAi: false })) ?? [], [data]);

    const exercises = useMemo(
        () =>
            ids
                .map((id) => {
                    if (isAiExerciseId(id)) {
                        return {
                            id: id,
                            nameEn: getAiExerciseNameFromId(id),
                            nameDe: getAiExerciseNameFromId(id),
                            isAi: true,
                        };
                    } else {
                        return traditionalExercise.find((x) => x.id === id);
                    }
                })
                .filter(isDefined),
        [ids, traditionalExercise],
    );

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <>
            <Typography color="primary" fontWeight="700" style={{ marginTop: 20, marginBottom: 10, fontSize: 20 }}>
                {header}
            </Typography>
            {exercises.length === 0 ? (
                <Typography textAlign="center">{noExercisePlaceholder}</Typography>
            ) : (
                <ul>
                    {exercises.map((exercise) => (
                        <EditListItem key={exercise.id} exercise={exercise} handleDelete={onDelete} />
                    ))}
                </ul>
            )}
        </>
    );
};

export default ExercisesEditList;
